import { useEffect, useRef, useState } from 'react';
import { CohortStepStatus, CohortStepType } from 'src/types/cohorts';
import { motion } from 'motion/react';
import { MaterialSymbol } from 'react-material-symbols';
import { ActionButtons } from '../action-buttons';
import { StreamingMarkdown } from 'src/components/common/streaming-markdown';

export interface DataExtractionStepProps {
  step: {
    data: { type: string; payload: { content: string; format: string } }[];
    type: CohortStepType;
    status: CohortStepStatus;
  };
  isActive: boolean;
  isCompleted: boolean;
  onAccept: () => void;
  onEdit: (args: { step: CohortStepType; editedContent: string }) => void;
  onTableRowDelete: (rowIndex: number) => void;
  onRetryPreviousStep: () => void;
}

export const DataExtractionStep = ({
  step,
  isActive,
  isCompleted,
  onAccept,
  onEdit,
}: DataExtractionStepProps) => {
  const stepContent = step.data.find((content) => content.type === 'text');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(stepContent?.payload.content || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      // Set the height to the scrollHeight to fit all content
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [editedContent]);

  const handleEdit = () => {
    setIsEditing(!isEditing);
    setEditedContent(stepContent?.payload.content ?? '');
  };

  const handleCloseEditPanel = () => {
    setIsEditing(false);
  };

  const handleEditAcceptClick = () => {
    if (onEdit && editedContent) {
      onEdit({ step: step.type, editedContent });
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
        className="relative mt-4 border border-gray-700 rounded-2xl p-6 bg-gray-900"
      >
        <div className="mb-8">
          <div className="text-sm pb-4 text-gray-500 font-normal">
            Edit your extraction parameters below. You can modify the text to better match your
            requirements.
          </div>

          <textarea
            ref={textareaRef}
            className="w-full bg-gray-900 text-white border-none focus:ring-0 focus:outline-none resize-none overflow-hidden"
            value={editedContent}
            autoFocus={true}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleEditAcceptClick();
              }
            }}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setEditedContent(e.target.value);
            }}
          />
        </div>

        <div className="absolute right-4 top-6">
          <button className="text-white" onClick={handleCloseEditPanel}>
            <MaterialSymbol icon="close" className="w-3 h-3" />
          </button>
        </div>

        <div className="absolute bottom-4 right-4 flex gap-4">
          <button
            className="bg-primary-background-default hover:bg-primary-background-hover text-black font-medium px-3 rounded-full text-sm h-fit py-2 border border-stroke-on-dark-subdued"
            onClick={handleEditAcceptClick}
          >
            <div className="flex flex-row items-center">
              <MaterialSymbol icon="magic_button" className="w-5 h-5 mr-1" />
              <span>Enhance</span>
            </div>
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className={`mb-6 text-white`}>
      {/* Render content based on type */}
      {step.data.map((content, contentIndex) => (
        <motion.div
          key={contentIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + contentIndex * 0.1, duration: 0.4 }}
          className="text-white"
        >
          <StreamingMarkdown content={content.payload.content} />
        </motion.div>
      ))}

      {isActive && isCompleted && <ActionButtons onAccept={onAccept} onEdit={handleEdit} />}
    </div>
  );
};
